import { useState, useEffect } from "react";
import { db } from "../firebase";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";

/**
 * Hook to fetch and manage projects from Firestore
 * Stores all projects as an array in a single document
 * @returns {object} { projects, loading, error, updateProjects }
 */
export function useProjects() {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch projects on mount
    useEffect(() => {
        async function fetchProjects() {
            try {
                setLoading(true);
                const docRef = doc(db, "projects", "projects");
                const docSnap = await getDoc(docRef);

                if (docSnap.exists()) {
                    const data = docSnap.data();
                    setProjects(data.projects || []);
                } else {
                    setError("No projects found");
                    setProjects([]);
                }
            } catch (err) {
                setError(err.message);
                setProjects([]);
            } finally {
                setLoading(false);
            }
        }

        fetchProjects();
    }, []);

    // Function to update projects
    const updateProjects = async (newProjects, userEmail) => {
        try {
            // Validation
            if (!Array.isArray(newProjects)) {
                throw new Error("Projects must be an array");
            }

            if (!userEmail) {
                throw new Error("User email required");
            }

            // Validate each project has required fields
            for (const project of newProjects) {
                if (!project.title || !project.description || !project.company || !project.year || !project.category) {
                    throw new Error("Each project must have: title, description, company, year, category");
                }
                // Ensure tags is an array
                if (!Array.isArray(project.tags)) {
                    project.tags = [];
                }
            }

            const docRef = doc(db, "projects", "projects");
            await setDoc(docRef, {
                page: "projects",
                projects: newProjects,
                lastUpdated: serverTimestamp(),
                updatedBy: userEmail,
            }, { merge: true });
            setProjects(newProjects);
            return { success: true };
        } catch (err) {
            setError(err.message);
            return { success: false, error: err.message };
        }
    };

    return { projects, loading, error, updateProjects };
}
